import clsx from 'clsx';
import type {
  CollectionState,
  SetInputNextSuggestion,
} from '@arcanum-hq/shared-types';
import type {
  RealtimeStatus,
  SyncStatus,
} from '@/hooks/use-conversation-channel';
import { NextFieldGuide } from './NextFieldGuide';
import { RealtimeStatusBadge } from './RealtimeStatusBadge';

interface BuilderToolbarProps {
  collected: number;
  pending: number;
  /**
   * Realtime channel state from `useConversationChannel`. Optional so callers
   * outside the active-channel context (e.g. tests) can omit it. The badge
   * auto-hides on `'connected'` to keep the toolbar clean during the happy
   * path. (model-view-ux-polish Phase 2 Session 2.2, 2026-04-27)
   *
   * Rehomed: relocated to the right of the compact-progress-bar widget row
   * (builder-ux-overhaul Phase 3 Session 2, 2026-05-22). Previously sat
   * inline next to the retired model-name / v{version} title block.
   * Route still guards: `activeConversationId ? realtimeStatus : undefined`.
   */
  realtimeStatus?: RealtimeStatus;
  /**
   * REST `/resync` health, surfaced as a "Syncing…" badge when WS is
   * `connected` but a cooldown is active. Phase 1.5 polish — see
   * `<RealtimeStatusBadge>` JSDoc.
   */
  syncStatus?: SyncStatus;
  /**
   * Server-computed collection-state (ADR 0002/0003, next-variable-curation
   * Phase 5). Forwarded to `<NextFieldGuide>` which renders the next group
   * label + count. Hidden when undefined or null, or when
   * collectionState.nextGroup is null (analysis stage).
   */
  collectionState?: CollectionState | null;

  // ---------------------------------------------------------------------------
  // DEPRECATED — builder-ux-overhaul Phase 3 Session 2 (2026-05-22).
  // These props were part of the retired title-block (model-name, v{version}
  // badge, deal-name span). The title block moved to <DealHeaderStrip>.
  // Props kept as optional no-ops so existing test files compile without
  // modification until Session 5 removes them alongside test updates.
  // ---------------------------------------------------------------------------
  /**
   * @deprecated next-variable-curation Phase 5 Session 5.1 will remove this.
   * Banner now reads collectionState (ADR 0003 coexist). The memo extraction
   * in builder.tsx stays but the toolbar no longer forwards this to
   * <NextFieldGuide>. Kept as a no-op so the existing builder.tsx mount
   * compiles until Session 5.1 updates the call site.
   */
  nextSuggestion?: SetInputNextSuggestion;
  /** @deprecated Moved to <DealHeaderStrip conversationTitle>. No-op. */
  title?: string;
  /** @deprecated Moved to <DealHeaderStrip signalVariant>. No-op. */
  modelName?: string | null;
  /** @deprecated Retired — schema version is now internal-only. No-op. */
  modelVersion?: string | null;
}

/**
 * builder-ux-overhaul Phase 3 Session 2 (2026-05-22) — title-block RETIRED.
 * The model-name, v{modelVersion} badge, and deal-name span have been moved
 * to `<DealHeaderStrip>` (mounted above this toolbar in builder.tsx) which is
 * the canonical Workbench deal header from now on. Schema version is
 * internal-only; the badge is gone from all user-facing surfaces.
 *
 * Surviving widget inventory (all 3 rehomed per spec WS1 §1):
 *   1. compact-progress-bar  — stays in this toolbar row as a standalone element.
 *   2. <NextFieldGuide>      — stays in this toolbar row (thin strip after progress).
 *   3. <RealtimeStatusBadge> — relocates to the RIGHT of the progress row (not in
 *      the LEFT-cluster pill seam — ADR 0006 Consequences).
 *
 * Phase 2 Session 6 simplification (original, preserved context):
 * - Replaced the 3-stat fragment with a single compact 5-cell `▓` block bar.
 * - Removed the vestigial "Complete" button (AcceptUnderwritingButton owns that).
 * - Compact bar lights cells at threshold `(i+1)*20`: 51% → 2 cells, 100% → all 5.
 */
export function BuilderToolbar({
  collected,
  pending,
  realtimeStatus,
  syncStatus,
  collectionState,
}: BuilderToolbarProps) {
  const total = collected + pending;
  const pct = total > 0 ? Math.round((collected / total) * 100) : 0;

  return (
    <div className="px-5 py-3.5 border-b border-panel-border flex items-center gap-5 shrink-0 bg-panel-bg">
      {/* Compact progress bar — relocates from the retired title-block row */}
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-micro font-mono text-text-dim tracking-[0.5px]">
          PROGRESS
        </span>
        <div
          data-testid="compact-progress-bar"
          className="flex items-center gap-1.5 px-2 py-0.5 rounded-[2px] bg-panel-bg border border-panel-border"
          aria-label={`${pct}% complete (${collected} of ${total} inputs)`}
        >
          <span className="font-mono text-micro" aria-hidden="true">
            {Array.from({ length: 5 }, (_, i) => (
              <span
                key={i}
                data-cell-index={i}
                data-cell-lit={pct >= (i + 1) * 20}
                className={clsx(
                  pct >= (i + 1) * 20 ? 'text-accent' : 'text-text-dim'
                )}
              >
                ▓
              </span>
            ))}
          </span>
          <span className="text-xs font-mono text-accent tabular-nums">
            {pct}%
          </span>
        </div>
      </div>

      {/* NextFieldGuide — thin group-suggestion strip; hides when collectionState absent or nextGroup null */}
      <NextFieldGuide collectionState={collectionState} />

      {/* RealtimeStatusBadge — RIGHT-cluster widget; auto-hides on 'connected' */}
      {realtimeStatus && (
        <RealtimeStatusBadge status={realtimeStatus} syncStatus={syncStatus} />
      )}
    </div>
  );
}
